/**
 * The case image's decode lifecycle: load it into an offscreen canvas, snapshot the pristine
 * pixels, publish the natural dimensions, and say so loudly when the load fails.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-6 / AUD-2 RC-4) with no change to a single body.
 *
 * Two things here are load-bearing well beyond this file:
 *  - `readSource`'s `try/catch` around `getImageData` is the TAINTED-CANVAS abort. J-16 removes a
 *    PNG encode on the strength of it, so `case-image-source.test.ts` asserts it directly rather
 *    than leaving it implied.
 *  - the `img.onerror` arm names the host and points at `docs/deploy-cors.md` + Force sync. A
 *    failed load used to leave the canvas SILENTLY blank, which is the face of every cross-device
 *    sync gap ("template comes blank on the other system"). It must never become a quiet return.
 *
 * The parts of the per-image reset that belong to state this hook does NOT own (the matte, the
 * marker cache, the display URL, the restore latches) are handed back through `onSourceResetRef`
 * — the same late-bound-ref pattern the editor already uses for `applyMatteToSource` and
 * `resetBackground`. J-7 moves that callback's body into `useMatte`; J-14 fills in `resetForView`.
 */
import { useEffect, useRef, useState } from "react";
import type { Point, Polygon } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import { snapQuadToGradient, toGray } from "./placement.js";
import { loadUncached } from "./uncachedImage.js";
import { clamp01, dataUrlToBytes, get2dContext } from "./pixels.js";

export interface CaseImageSourceArgs {
  /** The case image URL. A change re-runs the whole reset + decode. */
  url?: string;
  /** Display fallback width/height until the image's natural size is known. */
  fallbackWidth?: number;
  fallbackHeight?: number;
  /** `EditorRefs.sourceRef` — the offscreen canvas this hook fills. */
  sourceRef: React.RefObject<HTMLCanvasElement | null>;
  /** Surfaced to the parent so it can size the default brand rectangle. */
  onNaturalDims?: (d: { w: number; h: number }) => void;
  onLoadError?: () => void;
  setPlacementMsg: (m: string | null) => void;
  logger?: AppLogger;
  /**
   * The rest of the per-image reset — matte, marker cache, display URL, restore latches — owned by
   * hooks that do not exist yet at this point in the split. Assigned by the component AFTER those
   * refs are declared; invoked by the loader effect, which runs after render, so the late
   * assignment is always in place.
   */
  onSourceResetRef: React.RefObject<(() => void) | null>;
}

export function useCaseImageSource(args: CaseImageSourceArgs) {
  const {
    url,
    fallbackWidth,
    fallbackHeight,
    sourceRef,
    onNaturalDims,
    setPlacementMsg,
    logger,
    onSourceResetRef,
  } = args;

  // Natural pixel size of the loaded case image → drive the SVG at its true ASPECT so the image
  // renders edge-to-edge (no letterbox gap, no stretch) on non-square cases. Falls back to an
  // explicit prop, then a square default until the image loads. The whole coordinate system
  // (viewBox, overlays) derives from w/h, so they stay aligned.
  const [natDims, setNatDims] = useState<{ w: number; h: number } | null>(null);
  const w = natDims?.w ?? fallbackWidth ?? 512;
  const h = natDims?.h ?? fallbackHeight ?? 512;
  // Surface the resolved base-image dimensions so a parent can size the default brand rectangle.
  useEffect(() => {
    if (natDims) onNaturalDims?.(natDims);
  }, [natDims, onNaturalDims]);

  const [chromaReady, setChromaReady] = useState(false);
  /** The ORIGINAL generated PNG bytes (magenta marker intact) captured on load, so the
   *  magenta keyer always runs on the un-edited image even after a background removal. */
  const originalBytesRef = useRef<Uint8Array | null>(null);
  /** PRISTINE decoded case pixels (RGBA) captured once when the image is first drawn into the
   *  source canvas — before any removal mutates it. `applyMatteToSource` keys from THIS instead
   *  of the live canvas so re-applying the matte with different params (the calibration sliders)
   *  recomputes from the original image rather than compounding erosion on the already-keyed
   *  canvas. Re-armed with the same lifecycle as `originalBytesRef` (per case image). Null until
   *  the source canvas is ready, or when the canvas is unreadable (falls back to the live read). */
  const pristineRgbaRef = useRef<ImageData | null>(null);
  /** ED-4 — the case image failed to LOAD (404 / CORS / dead host), as opposed to never having
   *  been supplied. Surfaced on the bake handle so Save can name the real cause instead of
   *  telling the operator to press buttons that are disabled in exactly that state. */
  const caseImageFailedRef = useRef(false);
  /**
   * MN-11 / J-13 — the display size and the logger AS OF THIS RENDER, for the loader effect.
   *
   * The loader must re-run on a new URL and on nothing else. `w`/`h` are the DISPLAY fallbacks the
   * effect itself resolves (it calls `setNatDims`), so listing them would let the effect retrigger
   * on its own output and a container resize would silently re-decode the image. The comment under
   * the effect already said that; these refs make it TRUE instead of suppressed. `logger` is a prop
   * whose identity the parent owns, and a new logger must never restart a decode.
   */
  const dimsRef = useRef({ w, h });
  dimsRef.current = { w, h };
  const loggerRef = useRef(logger);
  loggerRef.current = logger;
  /** Same reasoning for the status setter: the loader WRITES it, so a caller that passes a fresh
   *  arrow each render must not thereby re-decode the image. (`MaskEditor` passes a `useState`
   *  setter, which is stable — but the hook must not depend on the caller being careful.) */
  const onLoadErrorRef = useRef(args.onLoadError);
  onLoadErrorRef.current = args.onLoadError;
  const setPlacementMsgRef = useRef(setPlacementMsg);
  setPlacementMsgRef.current = setPlacementMsg;

  // Load the case image into an offscreen canvas for pixel sampling. Feature-
  // detect canvas/getImageData (jsdom has neither) and no-op so tests don't crash.
  useEffect(() => {
    setChromaReady(false);
    // The matte / marker / display-URL half of the reset — see `onSourceResetRef`.
    onSourceResetRef.current?.();
    setPlacementMsgRef.current(null);
    setNatDims(null);
    sourceRef.current = null;
    originalBytesRef.current = null;
    pristineRgbaRef.current = null;
    caseImageFailedRef.current = false; // a new URL re-arms the load-failure signal (ED-4)
    if (!url || typeof document === "undefined") return;
    const canvas = document.createElement("canvas");
    const ctx = get2dContext(canvas);
    if (!ctx || typeof ctx.getImageData !== "function" || typeof Image === "undefined") return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      canvas.width = img.naturalWidth || dimsRef.current.w;
      canvas.height = img.naturalHeight || dimsRef.current.h;
      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Probe getImageData — tainted/unsupported canvases throw; no-op then.
        ctx.getImageData(0, 0, 1, 1);
        sourceRef.current = canvas;
        // Snapshot the PRISTINE decoded pixels ONCE (the canvas is untouched here) so matte
        // application always keys from the original image → idempotent re-apply for the
        // calibration sliders, instead of compounding erosion on the already-keyed canvas.
        // Armed BEFORE chromaReady flips so the reopen-restore replay (which waits on chromaReady)
        // always sees it. Best-effort: a tainted/unreadable canvas leaves it null (live-read fallback).
        try {
          pristineRgbaRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        } catch {
          pristineRgbaRef.current = null;
        }
        setNatDims({ w: canvas.width, h: canvas.height }); // display at the true image aspect
        setChromaReady(true);
        // Capture the pristine generated bytes (magenta marker intact) for the keyer.
        try {
          originalBytesRef.current = dataUrlToBytes(canvas.toDataURL("image/png"));
        } catch {
          originalBytesRef.current = null;
        }
      } catch {
        sourceRef.current = null;
        setChromaReady(false);
      }
    };
    // A failed load (404, CORS, dead host) used to leave the canvas SILENTLY blank — the
    // exact face of every cross-device sync gap ("template comes blank on the other
    // system"). Name the failure where the operator is looking, and log the URL host.
    img.onerror = () => {
      if (cancelled) return;
      onLoadErrorRef.current?.();
      sourceRef.current = null;
      caseImageFailedRef.current = true;
      setChromaReady(false);
      let host = url;
      try { host = new URL(url, "https://relative.invalid").host; } catch { /* keep raw */ }
      loggerRef.current?.log("error", `Editor · case image failed to load (${host}) — 404, CORS, or unreachable hosting. If this device never authored the template, check the R2 bucket CORS policy (docs/deploy-cors.md) and use Force sync.`);
      setPlacementMsgRef.current("The case image couldn't be loaded from hosting (404/CORS). Force sync this template; if it repeats, check the hosting CORS policy.");
    };
    void loadUncached(img, url);
    return () => {
      cancelled = true;
    };
    // J-13: this list is now COMPLETE. Only reload when the image URL changes; w/h are display
    // fallbacks (natDims is set here), so depending on them would re-trigger this loader on every
    // load — which is exactly why they are read through `dimsRef` rather than listed.
  }, [url, onSourceResetRef, sourceRef]);

  /** Read the source image's RGBA pixels at its natural resolution, or null. */
  const readSource = (): { data: Uint8ClampedArray; width: number; height: number } | null => {
    const canvas = sourceRef.current;
    const ctx = canvas ? get2dContext(canvas) : null;
    if (!canvas || !ctx) return null;
    try {
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      return { data: img.data, width: canvas.width, height: canvas.height };
    } catch {
      return null;
    }
  };

  /** Edge-snap a normalized 4-pt quad onto the source image's strongest gradients
   *  (Detection Phase 1): denormalize → snap → renormalize. Falls back to the input
   *  if it isn't a 4-corner quad. */
  const snapToEdges = (quad: Polygon, src: { data: Uint8ClampedArray; width: number; height: number }): Polygon => {
    if (quad.length !== 4) return quad;
    const gray = toGray(src.data, src.width, src.height);
    const px = quad.map((p) => ({ x: p.x * src.width, y: p.y * src.height })) as [Point, Point, Point, Point];
    const search = Math.max(3, Math.round(src.width * 0.012));
    const snapped = snapQuadToGradient(px, gray, src.width, src.height, search);
    return snapped.map((p) => ({ x: clamp01(p.x / src.width), y: clamp01(p.y / src.height) }));
  };

  /**
   * F-35 / J-14 — reset the per-view source state on a `view` prop change instead of a remount.
   *
   * Declared here, empty, by J-6 so the F-35 fix lands as one small diff against a settled seam
   * rather than re-opening this module. When two views share one reference image `caseImageUrl`
   * does not change, and skipping the re-decode is exactly the saving F-35 names.
   */
  const resetForView = () => {
    // Deliberately EMPTY of a re-decode. The loader keys on `caseImageUrl`, which is per-view
    // (`newRefUrl[activeView] ?? payloadRef ?? localUrl ?? bakedCaseUrl`), so switching to a view
    // with a different reference image re-decodes on its own. When two views SHARE one reference
    // image the decode is now skipped — and that skip is exactly the saving F-35 names. The matte
    // / marker / display half of the per-image reset still runs through `onSourceResetRef`.
  };

  return {
    natDims,
    /** Resolved display width — the image's natural width once decoded, else the fallback. */
    width: w,
    /** Resolved display height. */
    height: h,
    chromaReady,
    caseImageFailedRef,
    originalBytesRef,
    pristineRgbaRef,
    readSource,
    snapToEdges,
    resetForView,
  };
}
